import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  CameraIcon,
  ChatBubbleLeftIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
  PaperAirplaneIcon,
  PhotoIcon,
  DocumentTextIcon,
  PlusIcon,
  XMarkIcon,
  EyeIcon,
  TrashIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { authFetch } from '../api';

const EmployeeProjectDetails = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [issues, setIssues] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [workerHours, setWorkerHours] = useState([]);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [showHoursForm, setShowHoursForm] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [issueForm, setIssueForm] = useState({
    type: 'Quality',
    priority: 'Medium',
    description: ''
  });
  const [activityForm, setActivityForm] = useState({
    activityIndex: '',
    completed: 0,
    notes: ''
  });
  const [hoursForm, setHoursForm] = useState({
    date: new Date().toISOString().split('T')[0],
    hoursWorked: 0,
    notes: '',
    workerId: ''
  });
  const fileInputRef = useRef(null);
  const commentInputRef = useRef(null);
  const navigate = useNavigate();

  const fetchProjectData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check authentication
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        setError('No authentication token found. Please log in.');
        return;
      }
      
      // Fetch project data
      const projectDataResponse = await authFetch(`/employee/project/${projectId}`);
      
      console.log('🏗️ Project Data:', projectDataResponse);
      console.log('👥 Worker Hours:', projectDataResponse.workerHours);
      console.log('📋 Activities:', projectDataResponse.project?.activities);
      console.log('📊 Project Progress:', projectDataResponse.project?.percentCompleted);
      
      setProject(projectDataResponse.project);
      setWorkerHours(projectDataResponse.workerHours || []);
      
      // Set placeholder data for other sections (since those endpoints don't exist yet)
      setPhotos([]);
      setIssues([]);
      setComments([]);
      
    } catch (error) {
      console.error('Error fetching project:', error);
      
      if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('user');
      } else if (error.response?.status === 404) {
        setError('Project not found or you don\'t have access to it.');
      } else {
        setError('Failed to load project. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  const handleLogout = () => {
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('photos', file);
      });
      formData.append('projectId', projectId);
      formData.append('description', 'Project documentation');

      const response = await authFetch(`/employee/projects/${projectId}/photos`, {
        method: 'POST',
        body: formData,
        headers: {
          // Don't set Content-Type, let browser set it for FormData
        }
      });

      setPhotos(prev => [...prev, ...response]);
      setShowPhotoUpload(false);
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert('Failed to upload photos. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleIssueSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch(`/employee/projects/${projectId}/issues`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(issueForm)
      });

      setIssues(prev => [response, ...prev]);
      setShowIssueForm(false);
      setIssueForm({
        type: 'Quality',
        priority: 'Medium',
        description: ''
      });
    } catch (error) {
      console.error('Error submitting issue:', error);
      alert('Failed to submit issue. Please try again.');
    }
  };

  const handleActivitySubmit = async (e) => {
    e.preventDefault();
    
    // Validate that an activity is selected
    if (activityForm.activityIndex === '' || activityForm.activityIndex === null || activityForm.activityIndex === undefined) {
      alert('Please select an activity to log completion for.');
      return;
    }
    
    // Validate that completion amount is positive
    if (!activityForm.completed || activityForm.completed < 1) {
      alert('Please enter a valid completion amount of 1 or greater.');
      return;
    }

        try {
      console.log('🔍 Logging activity completion:', {
        projectId,
        activityIndex: activityForm.activityIndex,
        completed: activityForm.completed,
        notes: activityForm.notes
      });

      const requestBody = {
        completed: activityForm.completed,
        notes: activityForm.notes
      };
      
      console.log('📦 Request body being sent:', requestBody);
      console.log('📦 JSON.stringify result:', JSON.stringify(requestBody));

      const response = await authFetch(`/employee/projects/${projectId}/activities/${activityForm.activityIndex}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

            console.log('✅ Activity completion logged successfully:', response);

      // Update project activities locally with immediate UI update
      setProject(prev => {
        const updatedActivities = prev.activities.map((activity, index) => 
          index === parseInt(activityForm.activityIndex) 
            ? { 
                ...activity, 
                completed: (activity.completed || 0) + activityForm.completed,
                // Update progress percentage immediately
                progress: activity.planned && activity.planned > 0 
                  ? Math.round(((activity.completed || 0) + activityForm.completed) / activity.planned * 100)
                  : 0
              }
            : activity
        );

        // Calculate overall project progress using helper function
        const newPercentCompleted = calculateProjectProgress(updatedActivities);
        
        console.log('📊 Frontend progress calculation:', {
          newPercentCompleted
        });

        return {
          ...prev,
          activities: updatedActivities,
          percentCompleted: newPercentCompleted
        };
      });

      setShowActivityForm(false);
      setActivityForm({
        activityIndex: '',
        completed: 0,
        notes: ''
      });

      // Show success message with details
      const activityName = project.activities[parseInt(activityForm.activityIndex)]?.name || 'Activity';
      alert(`✅ Success! Logged ${activityForm.completed} units for "${activityName}".\n\nThe page will refresh automatically to show updated progress.`);
      
      // Refresh project data to ensure everything is in sync
      setTimeout(() => {
        console.log('🔄 Refreshing project data to sync with backend...');
        setRefreshing(true);
        fetchProjectData().finally(() => {
          setRefreshing(false);
        });
      }, 1000); // Slightly longer delay to let the user read the message
    } catch (error) {
      console.error('❌ Error logging activity:', error);
      
      // Provide more specific error messages
      if (error.response?.status === 404) {
        alert('Activity not found. Please refresh the page and try again.');
      } else if (error.response?.status === 403) {
        alert('Access denied. You are not assigned to this project.');
      } else if (error.response?.status === 400) {
        const errorMessage = error.response.data?.message || 'Invalid data provided. Please check your input.';
        console.error('Validation error details:', error.response.data);
        alert(`Validation Error: ${errorMessage}`);
      } else {
        alert('Failed to log activity. Please try again.');
      }
    }
  };

  const handleHoursSubmit = async (e) => {
    e.preventDefault();
    
    // Debug logging to ensure correct worker ID
    console.log('🔍 Submitting hours for worker:', {
      workerId: hoursForm.workerId,
      workerName: workerHours.find(w => w.worker._id === hoursForm.workerId)?.worker.name,
      date: hoursForm.date,
      hoursWorked: hoursForm.hoursWorked,
      notes: hoursForm.notes
    });
    
    try {
      const response = await authFetch(`/employee/project/${projectId}/log-hours`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(hoursForm)
      });

      // Refresh project data to get updated worker hours
      fetchProjectData();

      setShowHoursForm(false);
      setHoursForm({
        date: new Date().toISOString().split('T')[0],
        hoursWorked: 0,
        notes: '',
        workerId: ''
      });
    } catch (error) {
      console.error('Error logging hours:', error);
      alert('Failed to log hours. Please try again.');
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      const response = await authFetch(`/employee/projects/${projectId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: newComment,
          projectId: projectId
        })
      });

      setComments(prev => [response, ...prev]);
      setNewComment('');
      if (commentInputRef.current) {
        commentInputRef.current.focus();
      }
    } catch (error) {
      console.error('Error posting comment:', error);
      alert('Failed to post comment. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'On Track': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'At Risk': return 'bg-red-100 text-red-800 border-red-200';
      case 'Delayed': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getIssueTypeIcon = (type) => {
    switch (type) {
      case 'Safety': return '⚠️';
      case 'Quality': return '🔍';
      case 'Material': return '📦';
      case 'Equipment': return '🔧';
      default: return '📝';
    }
  };

  // Helper function to calculate project progress
  const calculateProjectProgress = (activities) => {
    let totalPlanned = 0;
    let totalCompleted = 0;
    
    activities.forEach(act => {
      totalPlanned += act.planned || 0;
      totalCompleted += act.completed || 0;
    });
    
    return totalPlanned > 0 ? Math.round((totalCompleted / totalPlanned) * 100) : 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#173861] border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading project details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex justify-center items-center p-4">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <div className="text-red-600 text-lg mb-4">{error}</div>
          <div className="space-x-4">
            <button
              onClick={() => navigate('/employee/dashboard')}
              className="bg-[#173861] text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => navigate('/login')}
              className="bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors duration-200"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Project not found</h1>
          <p className="text-gray-600 mb-4">The project you're looking for doesn't exist or you don't have access to it.</p>
          <button
            onClick={() => navigate('/employee/dashboard')}
            className="bg-[#173861] text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/employee/dashboard')}
                className="p-2 text-gray-600 hover:text-gray-900 transition-colors duration-200"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <div>
                <div className="flex items-center space-x-2">
                  <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
                  {refreshing && (
                    <div className="flex items-center space-x-2 text-blue-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                      <span className="text-sm">Refreshing...</span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-500">{project.powerCapacity}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowPhotoUpload(true)}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center"
              >
                <CameraIcon className="h-5 w-5 mr-2" />
                Add Photos
              </button>
              <button
                onClick={() => setShowIssueForm(true)}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors duration-200 flex items-center"
              >
                <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                Report Issue
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Project Status Banner */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className={`px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(project.status)}`}>
                {project.status}
              </span>
              <div className="flex items-center space-x-2 text-gray-600">
                <ClockIcon className="h-5 w-5" />
                <span>Progress: {project.percentCompleted || 0}%</span>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-gray-600">
              <MapPinIcon className="h-5 w-5" />
              <span>{project.location || 'Location not specified'}</span>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Overall Progress</span>
              <span className="text-sm text-gray-500">{project.percentCompleted || 0}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${project.percentCompleted || 0}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'overview', label: 'Overview', icon: DocumentTextIcon },
                { id: 'activities', label: 'Activities', icon: CheckCircleIcon },
                { id: 'workers', label: 'Workers & Hours', icon: UserIcon },
                { id: 'photos', label: 'Photos', icon: PhotoIcon },
                { id: 'issues', label: 'Issues', icon: ExclamationTriangleIcon },
                { id: 'comments', label: 'Comments', icon: ChatBubbleLeftIcon }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Details</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700">Power Capacity:</span>
                        <span className="text-gray-900">{project.powerCapacity}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700">Construction Type:</span>
                        <span className="text-gray-900">{project.constructionType}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700">Status:</span>
                        <span className={`px-2 py-1 rounded text-sm ${getStatusColor(project.status)}`}>
                          {project.status}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700">Progress:</span>
                        <span className="text-gray-900">{project.percentCompleted || 0}%</span>
                      </div>
                      {project.dueDate && (
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-700">Due Date:</span>
                          <span className="text-gray-900">{new Date(project.dueDate).toLocaleDateString('bg-BG')}</span>
                        </div>
                      )}
                    </div>
                  </div>


                </div>

                                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setActiveTab('photos')}
                      className="bg-blue-50 rounded-lg p-4 hover:bg-blue-100 transition-colors duration-200 cursor-pointer"
                    >
                      <div className="text-2xl font-bold text-blue-600">{photos.length}</div>
                      <div className="text-sm text-blue-600">Photos</div>
                    </button>
                    <button
                      onClick={() => setActiveTab('issues')}
                      className="bg-red-50 rounded-lg p-4 hover:bg-red-100 transition-colors duration-200 cursor-pointer"
                    >
                      <div className="text-2xl font-bold text-red-600">{issues.length}</div>
                      <div className="text-sm text-red-600">Issues</div>
                    </button>
                    <button
                      onClick={() => setActiveTab('comments')}
                      className="bg-green-50 rounded-lg p-4 hover:bg-green-100 transition-colors duration-200 cursor-pointer"
                    >
                      <div className="text-2xl font-bold text-green-600">{comments.length}</div>
                      <div className="text-sm text-green-600">Comments</div>
                    </button>
                    <button
                      onClick={() => setActiveTab('activities')}
                      className="bg-purple-50 rounded-lg p-4 hover:bg-purple-100 transition-colors duration-200 cursor-pointer"
                    >
                      <div className="text-2xl font-bold text-purple-600">
                        {project.activities?.length || 0}
                      </div>
                      <div className="text-sm text-purple-600">Activities</div>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Activities Tab */}
            {activeTab === 'activities' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Project Activities</h3>
                  <button
                    onClick={() => setShowActivityForm(true)}
                    className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Log Activity
                  </button>
                </div>
                
                <div className="space-y-4">
                  {Array.isArray(project.activities) && project.activities.length > 0 ? (
                    project.activities.map((activity, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h4 className="text-lg font-medium text-gray-900">{activity.name}</h4>
                            <div className="flex items-center space-x-4 mt-2">
                              <span className="text-sm text-gray-500">
                                {activity.completed || 0}/{activity.planned || 0} {activity.unit || 'units'}
                              </span>
                              <span className={`text-sm font-medium ${activity.targetColor || 'text-gray-600'}`}>
                                {activity.targetStatus || 'Target: ' + (activity.dailyTarget || 0)}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-lg font-bold ${activity.targetColor || 'text-gray-600'}`}>
                              {activity.progress || 0}%
                            </div>
                            <div className="text-sm text-gray-500">Progress</div>
                          </div>
                        </div>
                        
                        {/* Activity Progress Bar */}
                        <div className="mb-4">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-700">Progress</span>
                            <span className="text-sm text-gray-500">
                              {activity.progress || 0}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="h-2 rounded-full transition-all duration-300 bg-green-500"
                              style={{ 
                                width: `${Math.min(activity.progress || 0, 100)}%` 
                              }}
                            ></div>
                            <div 
                              className="h-2 rounded-full transition-all duration-300 bg-orange-500 -mt-2"
                              style={{ 
                                width: `${Math.min(100 - (activity.progress || 0), 100)}%`,
                                marginLeft: `${Math.min(activity.progress || 0, 100)}%`
                              }}
                            ></div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Unit:</span>
                            <span className="ml-2">{activity.unit || 'units'}</span>
                          </div>
                          <div>
                            <span className="font-medium">Daily Target:</span>
                            <span className="ml-2">{activity.dailyTarget || 'Not set'}</span>
                          </div>
                          <div>
                            <span className="font-medium">Completed:</span>
                            <span className="ml-2">{activity.completed || 0}</span>
                          </div>
                          <div>
                            <span className="font-medium">Planned:</span>
                            <span className="ml-2">{activity.planned || 0}</span>
                          </div>
                          <div>
                            <span className="font-medium">Remaining:</span>
                            <span className={`ml-2 font-semibold ${
                              (activity.planned || 0) - (activity.completed || 0) > 0 
                                ? 'text-orange-600' 
                                : 'text-green-600'
                            }`}>
                              {Math.max(0, (activity.planned || 0) - (activity.completed || 0))}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <CheckCircleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg mb-2">No activities defined</p>
                      <p className="text-gray-400">Activities will appear here once they're assigned to this project.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Workers & Hours Tab */}
            {activeTab === 'workers' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Workers & Hours</h3>
                </div>
                
                {/* Project Hours Summary */}
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <h4 className="text-lg font-semibold text-blue-900 mb-3">Project Hours Summary</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{project.totalWorkingHours || 0}</div>
                      <div className="text-sm text-blue-600">Working Hours</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{project.totalAdditionalHours || 0}</div>
                      <div className="text-sm text-green-600">Additional Work</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">{project.totalProjectHours || 0}</div>
                      <div className="text-sm text-purple-600">Total Hours</div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-6">
                  {workerHours.length > 0 ? (
                    workerHours.map((workerData, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h4 className="text-lg font-medium text-gray-900">
                              {workerData.worker.name}
                            </h4>
                            <p className="text-sm text-gray-500">{workerData.worker.email}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-blue-600">
                              {Math.round(workerData.totalHours * 100) / 100}
                            </div>
                            <div className="text-sm text-gray-500">Total Hours</div>
                          </div>
                        </div>
                        
                        {/* Recent Hours Entries */}
                        <div className="mt-4">
                          <h5 className="text-sm font-medium text-gray-700 mb-3">Recent Entries</h5>
                          <div className="space-y-2">
                            {workerData.entries.slice(0, 5).map((entry, entryIndex) => (
                              <div key={entryIndex} className="flex justify-between items-center text-sm">
                                <span className="text-gray-600">
                                  {new Date(entry.date).toLocaleDateString('bg-BG')}
                                </span>
                                <span className="font-medium text-gray-900">
                                  {entry.hoursWorked || 0} hours
                                </span>
                                {entry.notes && (
                                  <span className="text-gray-500 text-xs max-w-32 truncate">
                                    {entry.notes}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Log Hours Button for this worker */}
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <button
                            onClick={() => {
                              const selectedWorkerId = workerData.worker._id;
                              const selectedWorkerName = workerData.worker.name;
                              
                              console.log('🔍 Button clicked for worker:', {
                                workerId: selectedWorkerId,
                                workerName: selectedWorkerName,
                                currentHoursForm: hoursForm
                              });
                              
                              setHoursForm(prev => {
                                const newForm = { ...prev, workerId: selectedWorkerId };
                                console.log('🔍 Setting new hoursForm:', newForm);
                                return newForm;
                              });
                              
                              setShowHoursForm(true);
                            }}
                            className="w-full bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors duration-200 flex items-center justify-center"
                          >
                            <PlusIcon className="h-4 w-4 mr-2" />
                            Log Hours for {workerData.worker.name}
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <UserIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg mb-2">No workers assigned</p>
                      <p className="text-gray-400">Worker hours will appear here once they start logging time.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Photos Tab */}
            {activeTab === 'photos' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Project Photos</h3>
                  <button
                    onClick={() => setShowPhotoUpload(true)}
                    className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center"
                  >
                    <CameraIcon className="h-5 w-5 mr-2" />
                    Add Photos
                  </button>
                </div>
                
                {photos.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {photos.map((photo) => (
                      <div key={photo._id} className="relative group">
                        <img
                          src={photo.url}
                          alt={photo.description || 'Project photo'}
                          className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-75 transition-opacity duration-200"
                          onClick={() => setSelectedPhoto(photo)}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <EyeIcon className="h-6 w-6 text-white" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <PhotoIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg mb-2">No photos yet</p>
                    <p className="text-gray-400">Add photos to document project progress and milestones.</p>
                  </div>
                )}
              </div>
            )}

            {/* Issues Tab */}
            {activeTab === 'issues' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Project Issues</h3>
                  <button
                    onClick={() => setShowIssueForm(true)}
                    className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors duration-200 flex items-center"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Report Issue
                  </button>
                </div>
                
                <div className="space-y-4">
                  {issues.length > 0 ? (
                    issues.map((issue) => (
                      <div key={issue._id} className="bg-gray-50 rounded-lg p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <span className="text-2xl">{getIssueTypeIcon(issue.type)}</span>
                            <div>
                              <h4 className="text-lg font-medium text-gray-900">{issue.type} Issue</h4>
                              <p className="text-sm text-gray-500">
                                Reported {new Date(issue.createdAt).toLocaleDateString('bg-BG')}
                              </p>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(issue.priority)}`}>
                            {issue.priority} Priority
                          </span>
                        </div>
                        
                        <p className="text-gray-700 mb-4">{issue.description}</p>
                        
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <span>Status: {issue.status || 'Open'}</span>
                          <span>Reported by: {issue.reportedBy?.name || 'Unknown'}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <ExclamationTriangleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg mb-2">No issues reported</p>
                      <p className="text-gray-400">Report any issues you encounter during the project.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Comments Tab */}
            {activeTab === 'comments' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Project Comments</h3>
                
                {/* Comment Form */}
                <form onSubmit={handleCommentSubmit} className="mb-6">
                  <div className="flex space-x-4">
                    <div className="flex-1">
                      <textarea
                        ref={commentInputRef}
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows="3"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={!newComment.trim()}
                      className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                      Post
                    </button>
                  </div>
                </form>
                
                {/* Comments List */}
                <div className="space-y-4">
                  {comments.length > 0 ? (
                    comments.map((comment) => (
                      <div key={comment._id} className="bg-gray-50 rounded-lg p-6">
                        <div className="flex items-start space-x-3">
                          <UserIcon className="h-8 w-8 text-gray-400 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="font-medium text-gray-900">
                                {comment.author?.name || 'Unknown User'}
                              </span>
                              <span className="text-sm text-gray-500">
                                {new Date(comment.createdAt).toLocaleString('bg-BG')}
                              </span>
                            </div>
                            <p className="text-gray-700">{comment.content}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <ChatBubbleLeftIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg mb-2">No comments yet</p>
                      <p className="text-gray-400">Start the conversation by adding a comment.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Photo Upload Modal */}
      {showPhotoUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Upload Photos</h3>
              <button
                onClick={() => setShowPhotoUpload(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Photos
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {uploading && (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="text-sm text-gray-600 mt-2">Uploading photos...</p>
                </div>
              )}
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowPhotoUpload(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Issue Form Modal */}
      {showIssueForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Report Issue</h3>
              <button
                onClick={() => setShowIssueForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleIssueSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Issue Type
                  </label>
                  <select
                    value={issueForm.type}
                    onChange={(e) => setIssueForm(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Safety">Safety</option>
                    <option value="Quality">Quality</option>
                    <option value="Material">Material</option>
                    <option value="Equipment">Equipment</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={issueForm.priority}
                    onChange={(e) => setIssueForm(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={issueForm.description}
                    onChange={(e) => setIssueForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe the issue..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows="4"
                    required
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowIssueForm(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
                >
                  Report Issue
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Activity Form Modal */}
      {showActivityForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Log Activity</h3>
              <button
                onClick={() => setShowActivityForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleActivitySubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Activity
                  </label>
                  <select
                    value={activityForm.activityIndex}
                    onChange={(e) => setActivityForm(prev => ({ ...prev, activityIndex: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select an activity</option>
                    {project.activities?.map((activity, index) => (
                      <option key={index} value={index}>
                        {activity.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Completed Units
                  </label>
                  <input
                    type="number"
                    value={activityForm.completed}
                    onChange={(e) => setActivityForm(prev => ({ ...prev, completed: parseInt(e.target.value) || 0 }))}
                    min="1"
                    step="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={activityForm.notes}
                    onChange={(e) => setActivityForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Add any notes about this activity..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows="3"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowActivityForm(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200"
                >
                  Log Activity
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Hours Form Modal */}
      {showHoursForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            {/* Debug info */}
            <div className="mb-4 p-2 bg-yellow-100 rounded text-xs text-gray-700">
              <strong>Debug:</strong> workerId: {hoursForm.workerId}, 
              Worker: {workerHours.find(w => w.worker._id === hoursForm.workerId)?.worker.name || 'Unknown'}
            </div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Log Working Hours</h3>
              <button
                onClick={() => setShowHoursForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleHoursSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Worker
                  </label>
                  <div className="px-3 py-2 bg-gray-100 rounded-lg text-gray-700">
                    {workerHours.find(w => w.worker._id === hoursForm.workerId)?.worker.name || 'Unknown Worker'}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date
                  </label>
                  <input
                    type="date"
                    value={hoursForm.date}
                    onChange={(e) => setHoursForm(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hours Worked
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    max="24"
                    value={hoursForm.hoursWorked}
                    onChange={(e) => setHoursForm(prev => ({ ...prev, hoursWorked: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={hoursForm.notes}
                    onChange={(e) => setHoursForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Add any notes about the work..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows="3"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowHoursForm(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200"
                >
                  Log Hours
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Photo Viewer Modal */}
      {selectedPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <XMarkIcon className="h-8 w-8" />
            </button>
            <img
              src={selectedPhoto.url}
              alt={selectedPhoto.description || 'Project photo'}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            {selectedPhoto.description && (
              <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-50 text-white p-4 rounded-lg">
                <p>{selectedPhoto.description}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeProjectDetails;
